'use strict';

import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {useSelector} from 'react-redux';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';

import SunEditor from 'suneditor-react';

import {
    InputTextarea,
    InputTextareaProps
} from 'primereact/inputtextarea';

import {__} from '~/shared/src/utilities/messageCatalog';
import {
    TextFieldEditMode,
    TextFieldToolbarVisibility,
    useHookFormDelay
} from '~/shared/src/utilities/modules/form';

import FieldWrapper, {
    DefaultProps as FieldWrapperDefaultProps,
    defaultProps as fieldWrapperDefaultProps,
    Props as FieldWrapperProps,
    ComponentProps as FieldWrapperComponentProps,
    componentProps as fieldWrapperComponentProps
} from '~/frontend/src/components/base/shared/field/FieldWrapper';

import {StoreState} from '~/frontend/src/store';
import {colorPalette} from '~/frontend/src/components/base/shared/field/valueTypeFields/ColorField';


type InputProps = Omit<InputTextareaProps, 'autoComplete' | 'onChange' | 'key'>
const inputProps = keys<InputProps>();

type InputChangeProps = React.ChangeEvent<HTMLTextAreaElement>;

type DefaultProps = FieldWrapperDefaultProps & {

    /**
     * The edit mode.
     */
    editMode? : TextFieldEditMode;

    /**
     * The number of text rows to be displayed within the field.
     */
    height? : number;

    /**
     * The max number of text rows to be displayed within the field.
     * 0 means unlimited
     */
    maxHeight? : number;

    /**
     * Should the field marked if dirty.
     */
    markDirty? : boolean;

    /**
     * Should the toolbar never shown, shown while editing or always shown.
     */
    toolbarVisibility? : TextFieldToolbarVisibility;

    /**
     * The CSS class names to style the input field with.
     */
    inputClassNames? : string[];

    /**
     * The styles to style the input field with.
     */
    inputStyle? : React.CSSProperties;

    /**
     * true to use react-hook-form with debounced onChange event
     */
    useHookForm? : boolean;
};
const defaultProps : Required<DefaultProps> = {
    ...fieldWrapperDefaultProps,

    disabled         : false,
    editMode         : 'html',
    height           : 3,
    maxHeight        : 0,
    markDirty        : false,
    toolbarVisibility: 'whileEditing',
    inputClassNames  : [],
    inputStyle       : {},
    useHookForm      : false
};

type Props = Omit<FieldWrapperProps, 'pt'> & {

    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The value.
     */
    value? : string;

    id? : string;

    /**
     * The value to determine dirty against.
     */
    originalValue? : string;

    /**
     * Fired on change of input immediately if useHookForm is false, debounced if it's true
     * @param value
     * @param originalEvent
     */
    onChange : ( value : string | undefined, originalEvent : any ) => void;

    /**
     * Fired on change of input immediately if useHookForm is true otherwise never fired
     * @param value
     * @param originalEvent
     */
    onIntermediateChange? : ( value : string | undefined, originalEvent : any ) => void;

    pt? : {
        fieldWrapper? : FieldWrapperProps[ 'pt' ];
    };
};

export type ComponentProps = InputProps & DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = InputProps & Required<DefaultProps> & Props;


// field:
//   label:
//      font-size: 1rem
//      line-height: 1.2
//      margin-bottom: 0.5rem
//      => 1.2 * 1rem + 0.5rem
//      => 1.7rem
//   input:
//      font-size: 1rem
//      line-height: 1.2
//      border-top: 1px
//      border-bottom: 1px
//      padding-top: 0.5rem
//      padding-bottom: 0.5rem
//      => 1.2 * 1rem + 0.5rem + 0.5rem + 1px + 1px
//      => 2.2rem + 2px
//   message:
//      font-size: 0.85rem
//      line-height: 1.2
//      margin-top: 0.25rem
//      => 1.2 * 0.85rem + 0.25rem
//      => 1.27rem
//
// distance between fields:
//      margin-bottom: .5rem / 1rem
//      => .5rem / 1rem
export const fieldLabelHeightRem        = 1.7,
             fieldLabelHeightPx         = 0,
             fieldInputHeightRem        = 2.2,
             fieldInputHeightPx         = 2,
             fieldMessageHeightRem      = 1.27,
             fieldMessageHeightPx       = 0,
             fieldDistanceRem           = 0.5,
             fieldDistancePx            = 0,
             fieldAdditionalDistanceRem = 0.5,
             fieldAdditionalDistancePx  = 0;

export const getHeightStyle = ( inputStyle : AllProps[ 'inputStyle' ], height : AllProps[ 'height' ], maxHeight : AllProps[ 'maxHeight' ], labelVisibility : AllProps[ 'labelVisibility' ], labelPosition : AllProps[ 'labelPosition' ], infoTextLayout : AllProps[ 'infoTextLayout' ] ) : React.CSSProperties => {
    const retVal : React.CSSProperties = {},
          messageHeightRem : number    = infoTextLayout === 'text' ? fieldMessageHeightRem : 0,
          messageHeightPx : number     = infoTextLayout === 'text' ? fieldMessageHeightPx : 0;

    let fieldHeightRem : number = fieldInputHeightRem + messageHeightRem,
        fieldHeightPx : number  = fieldInputHeightPx + messageHeightPx,
        labelHeightRem : number = 0,
        labelHeightPx : number  = 0,
        distanceRem : number    = fieldDistanceRem,
        distancePx : number     = fieldDistancePx;

    if ( labelPosition === 'top' || labelPosition === 'bottom' || labelPosition === 'float' || labelPosition === 'placeholder' ) {
        fieldHeightRem += fieldLabelHeightRem;
        fieldHeightPx += fieldLabelHeightPx;

        if ( labelVisibility !== 'hidden' ) {
            labelHeightRem = fieldLabelHeightRem;
            labelHeightPx = fieldLabelHeightPx;
        }
    }

    if ( infoTextLayout !== 'text' ) {
        distanceRem += fieldAdditionalDistanceRem;
        distancePx += fieldAdditionalDistancePx;
    }

    if ( inputStyle.height === undefined && height ) {
        const styleHeightRem = height * fieldHeightRem + ( height - 1 ) * distanceRem - labelHeightRem - messageHeightRem,
              styleHeightPx  = height * fieldHeightPx + ( height - 1 ) * distancePx - labelHeightPx - messageHeightPx;

        retVal.height = `calc( ${styleHeightRem}rem + ${styleHeightPx}px )`;
    }

    if ( inputStyle.minHeight === undefined && retVal.height ) {
        retVal.minHeight = retVal.height;
    }

    if ( inputStyle.maxHeight === undefined && maxHeight ) {
        const styleMaxHeightRem = maxHeight * fieldHeightRem + ( maxHeight - 1 ) * distanceRem - labelHeightRem - messageHeightRem,
              styleMaxHeightPx  = maxHeight * fieldHeightPx + ( maxHeight - 1 ) * distancePx - labelHeightPx - messageHeightPx;

        retVal.maxHeight = `calc( ${styleMaxHeightRem}rem + ${styleMaxHeightPx}px )`;
    }

    return retVal;
};

// SunEditor strips <style> blocks unconditionally (hardcoded in its _deleteDisallowedTags),
// so we keep them out of the WYSIWYG: split them off before handing the content to SunEditor
// and prepend them again when emitting the value. This preserves <style> in the stored value
// (it is not rendered live inside the editor).
const styleBlockRegExp = /<style\b[^>]*>[\s\S]*?<\/style>/gi;

/**
 * Splits the <style> blocks off an HTML string.
 *
 * @param html the HTML to split
 * @returns the concatenated style blocks and the remaining body
 */
export const splitStyleBlocks = ( html : string | undefined ) : { styles : string; body : string } => {
    if ( !html ) {
        return { styles: '', body: html ?? '' };
    }

    const styles : string[] = [],
          body               = html.replace( styleBlockRegExp, ( match ) => {
              styles.push( match );

              return '';
          } );

    return { styles: styles.join( '' ), body };
};

const TextField = ( props : AllProps ) => {
    // avoid rendering if hidden
    if ( props.visibility === 'hidden' ) {
        return null;
    }
    else {
        const user                                  = useSelector( ( state : StoreState ) => state.session?.user ),
              {
                  uid,
                  disabled,
                  editMode,
                  height,
                  id,
                  infoTextLayout,
                  inputClassNames,
                  inputStyle,
                  labelPosition,
                  labelVisibility,
                  markDirty,
                  maxHeight,
                  messagePlaceholders,
                  messageTemplateStringResolver,
                  onBlur,
                  onChange,
                  onFocus,
                  onIntermediateChange,
                  originalValue,
                  placeholder,
                  pt,
                  tabIndex,
                  toolbarVisibility,
                  useHookForm,
                  value
              }                                     = props,
              elementId                             = id ?? `${uid}_textField`,
              inputRef                              = useRef<HTMLTextAreaElement>( null ),
              inValue                               = useRef<string | undefined>( value ),
              outValue                              = useRef<string | undefined>( value ),
              styleBlocks                           = useRef<string>( '' ),
              timeoutId                             = useRef<NodeJS.Timeout | undefined>(),
              [ elementValue, setElementValue ]     = useState<string | undefined>( value ),
              [ focus, setFocus ]                   = useState<boolean>( false ),
              [ cursorPosition, setCursorPosition ] = useState<number>( 0 ),
              editorLang : string                   = useMemo(
                  () => {
                      return user ? user.language.toLowerCase() : 'en';
                  },
                  [
                      user ? user.language : undefined
                  ]
              ),
              colorList : string[][]                = useMemo(
                  () => {
                      const retVal : string[][] = [];

                      for ( let i = 0; i < colorPalette.length; i++ ) {
                          for ( let k = 0; k < colorPalette[ i ].colors.length; k++ ) {
                              if ( !retVal[ k ] ) {
                                  retVal[ k ] = []
                              }

                              retVal[ k ][ i ] = colorPalette[ i ].colors[ k ];
                          }
                      }

                      return retVal;
                  },
                  [
                      'onlyOnce'
                  ]
              ),
              heightStyle                           = useMemo(
                  () => getHeightStyle( inputStyle, height, maxHeight, labelVisibility, labelPosition, infoTextLayout ),
                  [
                      height,
                      infoTextLayout,
                      inputStyle.height,
                      labelPosition,
                      labelVisibility,
                      maxHeight
                  ]
              ),
              messageResolver                       = useMemo(
                  () => {
                      if ( !messageTemplateStringResolver ) {
                          return ( messageKey : string ) : string | undefined => messageKey === '' ? undefined : __( messageKey, messagePlaceholders, false );
                      }
                      else {
                          return ( messageKey : string ) => messageKey === '' ? undefined : __( messageTemplateStringResolver( __( messageKey, messagePlaceholders ) ) ?? '', undefined, false );
                      }
                  },
                  [
                      messagePlaceholders,
                      messageTemplateStringResolver
                  ]
              ),
              resolvedPlaceholder                   = useMemo(
                  () => {
                      return placeholder ? messageResolver( placeholder ) : undefined;
                  },
                  [
                      JSON.stringify( placeholder ),
                      messageResolver,
                      user ? user.language : undefined
                  ]
              ),
              onSunEditorChange                     = useCallback(
                  ( changedBody : string ) => {
                      // SunEditor only sees the body; re-attach the style blocks we split off
                      const changedValue = styleBlocks.current + changedBody;

                      if ( changedValue !== outValue.current ) {
                          outValue.current = changedValue;

                          onChange( changedValue, undefined );
                      }
                  },
                  [
                      onChange
                  ]
              ),
              onHookFormSunEditorChange             = useCallback(
                  ( changedBody : string ) => {
                      // SunEditor only sees the body; re-attach the style blocks we split off
                      const changedValue = styleBlocks.current + changedBody;

                      if ( changedValue !== outValue.current ) {
                          outValue.current = changedValue;

                          onIntermediateChange?.( changedValue, undefined );

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }
                          timeoutId.current = setTimeout( onChange.bind( null, changedValue, undefined ), useHookFormDelay );
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange
                  ]
              ),
              onSunEditorFocus                      = useCallback(
                  ( event : FocusEvent ) => {
                      setFocus( true );

                      onFocus?.( event as unknown as React.FocusEvent<HTMLTextAreaElement> );
                  },
                  [
                      onFocus
                  ]
              ),
              onSunEditorBlur                       = useCallback(
                  ( event : FocusEvent ) => {
                      setFocus( false );

                      setElementValue( outValue.current );

                      if ( timeoutId.current ) {
                          clearTimeout( timeoutId.current );
                      }

                      if ( outValue.current !== inValue.current ) {
                          onChange( outValue.current, event );
                      }

                      onBlur?.( event as unknown as React.FocusEvent<HTMLTextAreaElement> );
                  },
                  [
                      onBlur,
                      onChange
                  ]
              ),
              onInputChange                         = useCallback(
                  ( event : InputChangeProps ) => {
                      const changedValue = event.target.value;

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );
                          setCursorPosition( 0 );

                          onChange( changedValue, event );
                      }
                  },
                  [
                      onChange
                  ]
              ),
              onHookFormInputChange                 = useCallback(
                  ( event : InputChangeProps ) => {
                      const changedValue = event.target.value;

                      if ( changedValue !== outValue.current ) {
                          setElementValue( changedValue );
                          setCursorPosition( 0 );

                          onIntermediateChange?.( changedValue, event );

                          if ( timeoutId.current ) {
                              clearTimeout( timeoutId.current );
                          }
                          timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange
                  ]
              ),
              onInputFocus                          = useCallback(
                  ( event : React.FocusEvent<HTMLTextAreaElement> ) => {
                      setFocus( true );

                      onFocus?.( event );
                  },
                  [
                      onFocus
                  ]
              ),
              onInputBlur                           = useCallback(
                  ( event : React.FocusEvent<HTMLTextAreaElement> ) => {
                      setFocus( false );

                      setElementValue( outValue.current );

                      if ( timeoutId.current ) {
                          clearTimeout( timeoutId.current );
                      }

                      if ( outValue.current !== inValue.current ) {
                          onChange( outValue.current, event );
                      }

                      onBlur?.( event );
                  },
                  [
                      onBlur,
                      onChange
                  ]
              ),
              onInputTextareaKey                    = useCallback(
                  ( event : React.KeyboardEvent<HTMLTextAreaElement> ) => {

                      // stop event on enter key
                      if ( event.key === 'Enter' ) {

                          // add a line break on enter
                          if ( event.type === 'keyup' ) {
                              const lineBreak         = '\n',
                                    currentValue      = ( inputRef.current as unknown as HTMLTextAreaElement )?.value,
                                    selectionStart    = ( inputRef.current as unknown as HTMLTextAreaElement )?.selectionStart,
                                    selectionEnd      = ( inputRef.current as unknown as HTMLTextAreaElement )?.selectionEnd,
                                    changedValue      = currentValue.slice( 0, selectionStart ) + lineBreak + currentValue.slice( selectionEnd ),
                                    newCursorPosition = selectionStart + lineBreak.length;

                              ( inputRef.current as unknown as HTMLTextAreaElement ).value = changedValue;
                              ( inputRef.current as unknown as HTMLTextAreaElement ).selectionStart = newCursorPosition;
                              ( inputRef.current as unknown as HTMLTextAreaElement ).selectionEnd = newCursorPosition;

                              setElementValue( changedValue );
                              setCursorPosition( ( inputRef.current as unknown as HTMLTextAreaElement )?.selectionStart ?? 0 );

                              if ( useHookForm ) {
                                  onIntermediateChange?.( changedValue, event );

                                  if ( timeoutId.current ) {
                                      clearTimeout( timeoutId.current );
                                  }
                                  timeoutId.current = setTimeout( onChange.bind( null, changedValue, event ), useHookFormDelay );
                              }
                              else {
                                  onChange( changedValue, event );
                              }
                          }

                          event.preventDefault();
                          event.stopPropagation();
                      }
                  },
                  [
                      onChange,
                      onIntermediateChange
                  ]
              ),
              getEditor                             = () : React.ReactElement => {
                  let editor : React.ReactElement;

                  if ( editMode === 'html' ) {
                      const hideToolbar = disabled || toolbarVisibility === 'hidden' || ( toolbarVisibility === 'whileEditing' && !focus ),
                            // keep <style> blocks out of the WYSIWYG (SunEditor would strip them)
                            { styles, body } = splitStyleBlocks( elementValue );

                      styleBlocks.current = styles;

                      editor = <div
                          id={elementId}
                          className={`yn-textfield yn-textfield-${editMode} ${inputClassNames.join( ' ' )} ${disabled ? 'yn-disabled' : ''} ${focus ? 'p-focus' : ''} ${hideToolbar ? 'yn-textfield-hide-toolbar' : ''}`}
                          style={{ ...defaultProps.inputStyle, ...inputStyle, ...heightStyle }}
                      >
                          <SunEditor
                              defaultValue={body}
                              setContents={body}
                              autoFocus={false}

                              onChange={useHookForm ? onHookFormSunEditorChange : onSunEditorChange}
                              onFocus={onSunEditorFocus}
                              onBlur={onSunEditorBlur}

                              disable={disabled}
                              height={'100%'}
                              // SunEditor type definition lacks of string for lang
                              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                              // @ts-ignore
                              lang={editorLang}
                              placeholder={resolvedPlaceholder}
                              setDefaultStyle="font-size: 1em; line-height: 1.2;"
                              setOptions={{
                                  buttonList     : [
                                      [
                                          'undo',
                                          'redo'
                                      ],
                                      [
                                          'fontSize'
                                      ],
                                      [
                                          'bold',
                                          'underline',
                                          'italic',
                                          'strike',
                                          'subscript',
                                          'superscript'
                                      ],
                                      [
                                          'fontColor',
                                          'hiliteColor',
                                          'outdent',
                                          'indent',
                                          'align',
                                          'horizontalRule',
                                          'list'
                                      ],
                                      [
                                          'link',
                                          'image'
                                      ],
                                      [
                                          'removeFormat',
                                          'codeView'
                                      ]
                                  ],
                                  resizingBar    : false,
                                  fontSize       : [
                                      .8,
                                      1,
                                      1.2,
                                      1.5,
                                      2
                                  ],
                                  fontSizeUnit   : 'em',
                                  mediaAutoSelect: false,
                                  // SunEditors type definition lacks string[][] for colorList
                                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                  // @ts-ignore
                                  colorList
                              }}
                          />
                      </div>;
                  }
                  else {
                      editor = <InputTextarea
                          {..._.pick( props, inputProps ) as Omit<InputProps, 'onChange'>}

                          id={elementId}

                          ref={inputRef}

                          value={elementValue ?? ''}

                          onChange={useHookForm ? onHookFormInputChange : onInputChange}

                          pt={{
                              root: {
                                  onFocus  : onInputFocus,
                                  onBlur   : onInputBlur,
                                  onKeyUp  : onInputTextareaKey,
                                  onKeyDown: onInputTextareaKey
                              }
                          }}

                          className={`yn-textfield yn-textfield-${editMode} ${inputClassNames.join( ' ' )}`}
                          style={{ ...defaultProps.inputStyle, ...inputStyle, ...heightStyle }}

                          placeholder={resolvedPlaceholder}
                          tooltip={undefined}
                          tooltipOptions={undefined}

                          aria-describedby={`${elementId}_message`}
                      />;
                  }

                  return editor;
              };

        inValue.current = value;

        if ( editMode === 'html' ) {
            outValue.current = value;

        }
        if ( editMode === 'plaintext' ) {
            outValue.current = elementValue;
        }

        useEffect(
            () => {
                if ( !focus ) {
                    setElementValue( value );
                }
            },
            [
                value
            ]
        );

        // set cursor position
        // set height if autoHeight
        useLayoutEffect(
            () => {
                if ( inputRef.current ) {
                    const editor = inputRef.current;

                    if ( cursorPosition !== 0 ) {
                        editor.setSelectionRange( cursorPosition, cursorPosition );
                        setCursorPosition( 0 );
                    }

                    if ( height === 0 ) {
                        if ( editor?.scrollHeight ) {
                            editor.style.height = '2.2rem';
                            editor.style.height = `${editor.scrollHeight}px`;
                        }
                    }
                }
            }
        );

        // set tab index for SunEditor
        useEffect(
            () => {
                if ( editMode === 'html' && tabIndex ) {
                    const editor = ( document.getElementById( elementId )?.querySelector( '.sun-editor-editable' ) as HTMLElement | undefined );

                    if ( editor && editor?.tabIndex !== tabIndex ) {
                        editor.tabIndex = tabIndex;
                    }
                }
            }
        );

        return <FieldWrapper
            {..._.pick( props, fieldWrapperComponentProps ) as FieldWrapperComponentProps}

            uid={elementId}

            isDirty={markDirty && ( elementValue ?? '' ) !== ( originalValue ?? '' )}

            autoComplete={false}
            pt={pt?.fieldWrapper}
        >
            {getEditor()}
        </FieldWrapper>;
    }
};
TextField.defaultProps = defaultProps;

export default TextField;
